import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { DeliveryPoint } from "../thunks/delivery.thunks";
import {
  isAddressValid,
  isValidDate,
  SPARv2ValidAddress,
} from "../types/delivery";
// import type { DeliveryPoint, ShippingMethod } from "../types/delivery";

// Cache for selectSPARv2Address to ensure referential stability
// This prevents infinite re-renders when used as useEffect dependency
let cachedSPARv2Result: SPARv2ValidAddress | null = null;
let cachedAddressKey: string | null = null;

// Base selector
const selectDeliveryState = (state: RootState) => state.delivery;

// Address selectors
export const selectDeliveryAddress = createSelector(
  [selectDeliveryState],
  (delivery) => delivery.address
);

export const selectAddressValidation = createSelector(
  [selectDeliveryState],
  (delivery) => ({
    isValid: delivery.validateAddressResponse.valid,
    loading: delivery.validateAddressLoading,
    error: delivery.error,
  })
);

export const selectSPARv2Address = createSelector(
  [
    (state: RootState) =>
      state.bankId.verificationDetails?.userAttributes?.SPARv2,
  ],
  (sparv2): SPARv2ValidAddress | null => {
    try {
      if (!sparv2?.ns15Folkbokforingsadress) {
        // Reset cache when no data
        if (cachedAddressKey !== null) {
          cachedAddressKey = null;
          cachedSPARv2Result = null;
        }
        return null;
      }

      const now = new Date();

      // Handle both array and single object cases
      const addresses = Array.isArray(sparv2.ns15Folkbokforingsadress)
        ? sparv2.ns15Folkbokforingsadress
        : [sparv2.ns15Folkbokforingsadress];

      // Find current valid address by checking date range
      const currentAddress = addresses.find((addr) => {
        const svenskAdress = addr?.ns3SvenskAdress;
        if (!svenskAdress) return false;

        const fromDate = new Date(svenskAdress.ns2DatumFrom || "");
        const tillDate = new Date(svenskAdress.ns2DatumTill || "");

        // Validate dates
        if (
          !isValidDate(svenskAdress.ns2DatumFrom || "") ||
          !isValidDate(svenskAdress.ns2DatumTill || "")
        ) {
          return false;
        }

        // Check if current date is within range
        return now >= fromDate && now <= tillDate;
      });

      if (!currentAddress?.ns3SvenskAdress) {
        // Reset cache when no valid address
        if (cachedAddressKey !== null) {
          cachedAddressKey = null;
          cachedSPARv2Result = null;
        }
        return null;
      }

      const address = currentAddress.ns3SvenskAdress;

      // Validate all required fields
      if (!isAddressValid(address)) {
        // Reset cache when invalid
        if (cachedAddressKey !== null) {
          cachedAddressKey = null;
          cachedSPARv2Result = null;
        }
        return null;
      }

      // Create cache key from address values
      const addressKey = `${address.ns3Utdelningsadress2}|${address.ns3PostNr}|${address.ns3Postort}`;

      // Return cached result if address hasn't changed (referential stability)
      if (addressKey === cachedAddressKey && cachedSPARv2Result !== null) {
        return cachedSPARv2Result;
      }

      // Create and cache new result
      cachedSPARv2Result = {
        street: address.ns3Utdelningsadress2.trim(),
        postalCode: address.ns3PostNr.trim(),
        city: address.ns3Postort.trim(),
        isValid: true,
      };
      cachedAddressKey = addressKey;

      return cachedSPARv2Result;
    } catch (error) {
      console.error("Error processing SPARv2 address:", error);
      // Reset cache on error
      cachedAddressKey = null;
      cachedSPARv2Result = null;
      return null;
    }
  }
);

// Delivery points selectors
export const selectServicePoints = createSelector(
  [selectDeliveryState],
  (delivery): DeliveryPoint[] => delivery.servicePoints
);

export const selectParcelBoxes = createSelector(
  [selectDeliveryState],
  (delivery): DeliveryPoint[] => delivery.parcelBoxes
);

// Selected delivery method
export const selectDeliveryMethod = createSelector(
  [selectDeliveryState],
  (delivery) => ({
    selectedMethod: delivery.selectedMethod,
    selectedServicePoint: delivery.selectedServicePoint,
    selectedParcelBox: delivery.selectedParcelBox,
  })
);

// Shipping methods
export const selectShippingMethods = createSelector(
  [selectDeliveryState],
  (delivery) => delivery.shippingMethods
);

// Loading states
export const selectDeliveryLoadingStates = createSelector(
  [selectDeliveryState],
  (delivery) => ({
    validateAddress: delivery.validateAddressLoading,
    servicePoints: delivery.servicePointsLoading,
    parcelBoxes: delivery.parcelBoxesLoading,
    shipping: delivery.shippingMethodsLoading,
    createAddress: delivery.createDeliveryAddressLoading,
    selectMethod: delivery.selectDeliveryMethodLoading,
  })
);

// Combined status
export const selectDeliveryStatus = createSelector(
  [selectDeliveryLoadingStates, selectDeliveryState],
  (loading, delivery) => ({
    isLoading: Object.values(loading).some((state) => state === "pending"),
    hasError: !!delivery.error,
    isMethodSelected: !!delivery.selectedMethod,
    isAddressValid: delivery.validateAddressResponse.valid,
  })
);
